/**
 * LearningHub.tsx — the Learning Hub (LH-F07..F16), on the design system.
 *
 * THE PAGE IS THE FRAME PROFILE USES; THE DIALOGS ARE THE FRAME UPLOAD USES.
 * Both finished designs are borrowed rather than approximated: the page wears
 * `.lh-*` (learningPage.css), which is `.pp-*`'s geometry — a full-bleed 193px
 * navy band flush under the header, then sections on one 64px inset, each a 32px
 * head row with a right-pinned control — and all three dialogs are the upload
 * flow's real `OverlayFrame` + `ModalShell`, so they are the same 1056×640 box,
 * the same scrim, the same header/footer insets as the wizard. What was 121 lines
 * of `LEARNING_STYLES`, 57 `.tb-learn-*` classes on the V1 `--tb-*` palette and
 * 37 inline `style={{}}` objects is now four type sizes and two weights.
 *
 * WHAT MOVED INTO A DIALOG. Three things that were long, self-contained and read
 * once: the mandatory training, the self-assessment, and the About/FAQ prose. The
 * Microsoft Learn list stays inline — it is what the page is for — as does Real
 * examples. Nothing was moved to hide it: every dialog is reachable from a labelled
 * control in the head row of the section it belongs to.
 *
 * THE POWERLESS INVARIANT, RENDERED (PC-F01 / LH-F09 / LH-NFR04). The competency
 * level is a LENS on ordering and framing only — never a gate:
 *   - The level row is a pressable pill per level (a lens you can change) with the
 *     stored one marked "— yours", under one line that says so in words.
 *   - The self-assessment result is ALWAYS labelled self-assessed and access-neutral
 *     right where it is shown.
 *   - The topic pills bypass personalisation entirely (LH-NFR03), and the level lens
 *     only re-orders the client's copy of the list — it never hides an item.
 * The server already guarantees this (readCachedItems biases, never filters); the UI
 * never adds a gate the server refused to add.
 *
 * NEVER BLOCK ON MICROSOFT LEARN (LH-F03 / LH-NFR01 / LH-NFR06). The loader reads the
 * hub payload, which is cache-only server-side, so the page renders even when Learn
 * is unreachable. Search debounces 300ms and falls back to client-filtering the
 * cached items; staleness and a failing refresh are stated in a line, never as an
 * empty or spinning content area.
 *
 * OUTBOUND LINKS (LH-F05 / LH-F13). Every Learn link states it leaves the platform,
 * opens in a new tab with rel="noopener", and fires a fire-and-forget recordLinkOpen
 * — the click never waits on it.
 *
 * ACCESSIBILITY (LH-NFR02). Training progress has a text equivalent and no longer
 * has a progress ring at all, so a shape can never be its sole carrier. All three
 * dialogs are real dialogs — role="dialog" aria-modal from OverlayFrame, Tab trapped
 * and Escape handled by useLearningModal, focus restored to the control that opened
 * them via `returnFocusRef` (the house pattern from Profile's panels).
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { AnimatePresence } from 'motion/react';
import { useLoaderData, useRevalidator } from 'react-router';
import { formatAdopterCount } from '../tools/trustSignals';
import { KombiJourney } from '../design/KombiJourney';
import { Button } from '../tools/upload/design/Button';
import { Pill } from '../tools/upload/design/Pill';
import { SearchInput } from '../tools/upload/design/SearchInput';
import { AssessmentDialog, FaqDialog, LEVELS, TrainingDialog } from './dialogs';
import { isStale, shortDate } from './staleness';
import { buildIntentTiles, buildTopics, TRAINING_STOPS } from './hubModel';
import {
  getHub,
  getExamples,
  searchLearn,
  recordLinkOpen,
  type CompetencyLevel,
  type HubPayload,
  type ExamplesPayload,
  type LearnItem,
  type TrainingProgress,
} from './api';
import '../kombi/Kombi.css';
import '../learningPage.css';

// ── loader ───────────────────────────────────────────────────────────────────────────────────

export type LearningLoaderData = { state: 'ok'; hub: HubPayload } | { state: 'signedOut' };

/**
 * One call hydrates the page. A 401 is a real signed-out state (the Learning family is always
 * mounted), so it renders the sign-in prompt rather than throwing.
 */
export async function learningHubLoader(): Promise<LearningLoaderData> {
  const res = await getHub();
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') throw new Error(`GET /api/learning/hub failed (${res.status})`);
  return { state: 'ok', hub: res.body };
}

// The three dialogs, the level lists and the staleness helpers now live beside this file so the Kombi
// Learning page shares them rather than owning a second copy — see dialogs.tsx's header.

// ── page ─────────────────────────────────────────────────────────────────────────────────────

export function LearningHub() {
  const data = useLoaderData() as LearningLoaderData;
  if (data.state === 'signedOut') return <SignedOutLearning />;
  return <LearningHubBody hub={data.hub} />;
}

function LearningHubBody({ hub }: { hub: HubPayload }) {
  const revalidator = useRevalidator();

  // Which dialog is open, if any. Null = the page itself.
  const [dialog, setDialog] = useState<null | 'assessment' | 'training' | 'faq'>(null);

  // The "view as any level" lens (LH-F09). Defaults to the person's self-assessed level, or Beginner
  // when they have not assessed. This ONLY re-orders the client's copy of the items — it never hides
  // an item, and it never touches the server or the person's stored level.
  const [viewLevel, setViewLevel] = useState<CompetencyLevel>(hub.competencyLevel ?? 'Beginner');

  // Keep the lens in step with a freshly-saved assessment after a revalidate. This is the
  // React-recommended "adjust state while rendering" pattern (not a setState-in-effect): when the
  // server-side self-assessed level changes under us, snap the lens to it before we paint.
  const [lastSeenLevel, setLastSeenLevel] = useState<CompetencyLevel | null>(hub.competencyLevel);
  if (hub.competencyLevel && hub.competencyLevel !== lastSeenLevel) {
    setLastSeenLevel(hub.competencyLevel);
    setViewLevel(hub.competencyLevel);
  }

  // One ref per dialog trigger. A dialog restores focus to the control that opened it, so a keyboard
  // user lands back where they were rather than at the top of the document (WCAG 2.4.3).
  const trainingTrigger = useRef<HTMLButtonElement>(null);
  const assessTrigger = useRef<HTMLButtonElement>(null);
  const faqTrigger = useRef<HTMLButtonElement>(null);

  const closeDialog = useCallback(() => setDialog(null), []);

  const onAssessed = useCallback(() => {
    setDialog(null);
    revalidator.revalidate();
  }, [revalidator]);

  const onTrainingProgress = useCallback(() => {
    revalidator.revalidate();
  }, [revalidator]);

  return (
    <section aria-labelledby="learning-heading" className="tb-learning lh-page kombi-app">
      {/* ── Band: the page's name and what tunes it (left), the mandatory training (right). ── */}
      <div className="lh-band-bleed">
        <div className="lh-band">
          <div className="lh-band__main">
            <h1 id="learning-heading">Learning</h1>
            {hub.department && (
              // LH-F10: the directory-sourced job function, read-only and named for transparency. It
              // tunes relevance (own-department examples first); it changes nothing about access.
              <p className="lh-band__dept">
                Examples for {hub.department} first — from your directory profile, read-only.
              </p>
            )}
          </div>
          <TrainingCard
            training={hub.training}
            triggerRef={trainingTrigger}
            onOpen={() => setDialog('training')}
          />
        </div>
      </div>

      {/* ── Where do you want to go? The level lens + the intent tiles it re-frames (LH-F09). ── */}
      <IntentSection
        viewLevel={viewLevel}
        storedLevel={hub.competencyLevel}
        assessed={hub.assessed}
        onViewLevel={setViewLevel}
        mostPopularTool={hub.mostPopularTool}
        triggerRef={assessTrigger}
        onOpenAssessment={() => setDialog('assessment')}
      />

      {/* ── Learn with Microsoft Learn: ONE list, driven by search + topic pills. ── */}
      <LearnSection learn={hub.learn} viewLevel={viewLevel} />

      {/* ── Real examples: genuine, approved catalogue tools (LH-F16). Degrades silently. ── */}
      <RealExamples />

      {/* ── About Trailblazer: the FAQ lives in a dialog. The section keeps `id="learn-with-people"`
             so existing deep links still land (LEARNING_PAGE_DESIGN_SPEC.md:284-285), and it names
             the department support champion in one line — the "Learn in person" tile promises a
             person, so the route to one has to be on the page, not only inside a dialog (UR-F10). ── */}
      <section id="learn-with-people" aria-labelledby="about-heading" className="lh-section">
        <div className="lh-section-head">
          <h2 id="about-heading">About Trailblazer</h2>
        </div>
        <div className="lh-controls">
          <div className="lh-controls__row">
            <Button
              ref={faqTrigger}
              variant="outline-square"
              size="sm"
              className="lh-controls__trailing"
              aria-haspopup="dialog"
              onClick={() => setDialog('faq')}
            >
              Read the FAQ
            </Button>
          </div>
        </div>
        <p className="lh-note">
          Prefer a person? Start with your department support champion — you can find yours from
          your{' '}
          <a className="lh-prose-link" href="/me">
            profile
          </a>
          , or ask in the{' '}
          <a className="lh-prose-link" href="/community">
            Community
          </a>
          .
        </p>
      </section>

      <p className="lh-footnote">
        The Microsoft Learn content here is demonstration content for this preview; the links open
        the genuine pages.
      </p>

      <AnimatePresence>
        {dialog === 'training' && (
          <TrainingDialog
            key="training"
            onClose={closeDialog}
            onProgress={onTrainingProgress}
            returnFocusRef={trainingTrigger}
          />
        )}
        {dialog === 'assessment' && (
          <AssessmentDialog
            key="assessment"
            currentLevel={hub.competencyLevel}
            currentMakerType={hub.makerType}
            onClose={closeDialog}
            onAssessed={onAssessed}
            returnFocusRef={assessTrigger}
          />
        )}
        {dialog === 'faq' && (
          <FaqDialog key="faq" onClose={closeDialog} returnFocusRef={faqTrigger} />
        )}
      </AnimatePresence>
    </section>
  );
}

/** The signed-out prompt. Same band, so the page does not change shape when you sign in. */
function SignedOutLearning() {
  return (
    <section aria-labelledby="signin-heading" className="tb-learning lh-page kombi-app">
      <div className="lh-band-bleed">
        <div className="lh-band">
          <div className="lh-band__main">
            <h1 id="signin-heading">Learning</h1>
            <p className="lh-band__lede">
              Trailblazer talks to Microsoft only through our own backend — this page never sees
              your password or any token.
            </p>
          </div>
        </div>
      </div>
      <p className="lh-footnote">
        <a className="lh-signin" href="/auth/login?returnTo=/learning">
          Sign in with your work account
        </a>
      </p>
    </section>
  );
}

// ── the mandatory-training card (band, right) ────────────────────────────────────────────────
/**
 * The training's state in ONE line of words — state, steps done, and whether the check is passed.
 * That sentence carries the value; the journey below it is reinforcement, never the sole carrier
 * (LH-NFR02). The old SVG dial is gone either way: this redesign and the Kombi journey that landed
 * on staging each removed it independently, for the same reason.
 */
function TrainingCard({
  training,
  triggerRef,
  onOpen,
}: {
  training: TrainingProgress;
  triggerRef: RefObject<HTMLButtonElement>;
  onOpen: () => void;
}) {
  const stateLabel =
    training.state === 'complete'
      ? 'Complete'
      : training.state === 'in_progress'
        ? 'In progress'
        : 'Not started';
  const cta =
    training.state === 'complete'
      ? 'Review'
      : training.state === 'in_progress'
        ? 'Resume'
        : 'Start';

  return (
    <div className="lh-training">
      <p className="lh-training__title">AI Awareness training</p>
      <p className="lh-training__state">
        {stateLabel} · {training.stepsCompleted} of {training.stepCount} steps
        {training.knowledgeCheckPassed ? ' · check passed' : ''}
      </p>
      {/* The training as a road — the Kombi journey that landed on staging, kept here inside the
          redesigned card. `showLabels={false}` keeps the step names in the list for assistive tech
          while `.lh-training__state` above states the position in words, so nothing here is the sole
          carrier of the value (LH-NFR02). Staging's second copy of that sentence (a
          `.tb-learn-progress-text` block) is dropped rather than restyled — the line above already
          says it, in the redesign's own type.

          The van sits at `stepsCompleted`, which is the step you are ON — the one after the last you
          finished — clamped so a final completed step does not ask for a stop past the end.
          `complete` is the SERVER's state, not a step count: the course is done only once the
          knowledge check is passed too. */}
      <KombiJourney
        stops={TRAINING_STOPS(training.stepCount)}
        current={Math.min(training.stepsCompleted, Math.max(training.stepCount - 1, 0))}
        complete={training.state === 'complete'}
        showLabels={false}
        label="Training progress"
        vanHeight={34}
      />
      <Button
        ref={triggerRef}
        variant="solid-square"
        size="sm"
        className="lh-btn-navy lh-training__cta"
        aria-haspopup="dialog"
        onClick={onOpen}
      >
        {cta}
      </Button>
    </div>
  );
}

// ── "Where do you want to go?" — the level lens + the intent tiles ───────────────────────────
/**
 * The journey band, the "view as" select and the intent tiles, collapsed into ONE section: a row of
 * pressable level pills IS the lens control, and the tiles beneath it re-frame as you press. The
 * three widgets said the same thing three times; one row says it once.
 *
 * Pressing a pill changes the HINTS only. The stored level is marked "— yours" and `aria-current`, so
 * looking through another level's lens is visibly a look, not a change.
 */
function IntentSection({
  viewLevel,
  storedLevel,
  assessed,
  onViewLevel,
  mostPopularTool,
  triggerRef,
  onOpenAssessment,
}: {
  viewLevel: CompetencyLevel;
  storedLevel: CompetencyLevel | null;
  assessed: boolean;
  onViewLevel: (level: CompetencyLevel) => void;
  mostPopularTool: HubPayload['mostPopularTool'];
  triggerRef: RefObject<HTMLButtonElement>;
  onOpenAssessment: () => void;
}) {
  const tiles = buildIntentTiles(mostPopularTool ? `/tools/${mostPopularTool.toolId}` : '/tools');

  return (
    <section aria-labelledby="intent-heading" className="lh-section">
      <div className="lh-section-head">
        <h2 id="intent-heading">Where do you want to go?</h2>
      </div>

      <div className="lh-controls">
        <div className="lh-controls__row">
          <div className="lh-pills" role="group" aria-label="View the hints for a level">
            {LEVELS.map((level) => {
              const viewing = level === viewLevel;
              const yours = level === storedLevel;
              return (
                <Pill
                  key={level}
                  as="button"
                  className="lh-pill"
                  aria-pressed={viewing}
                  aria-current={yours ? 'true' : undefined}
                  onClick={() => onViewLevel(level)}
                >
                  {level}
                  {yours ? ' — yours' : ''}
                </Pill>
              );
            })}
          </div>
          <Button
            ref={triggerRef}
            variant="outline-square"
            size="sm"
            className="lh-controls__trailing"
            aria-haspopup="dialog"
            onClick={onOpenAssessment}
          >
            {assessed ? 'Re-take the self-assessment' : 'Take the self-assessment'}
          </Button>
        </div>
        {/* LH-F08 is a Must: the level is labelled self-assessed WHEREVER it appears, and the
            pill row above is where it appears now that the journey band is gone. `assessed` is
            exactly `competencyLevel !== null` server-side, so this never asserts a level to
            someone who has not taken the questions. */}
        <p className="lh-note">
          {assessed
            ? 'Your level is self-assessed. Hints only — nothing here changes your access.'
            : 'Hints only — nothing here changes your access.'}
        </p>
      </div>

      <ul className="lh-tiles">
        {tiles.map((tile) => (
          <li key={tile.title}>
            {tile.external ? (
              <a
                className="lh-tile"
                href={tile.to}
                target="_blank"
                rel="noreferrer"
                data-testid="intent-tile"
              >
                <div className="lh-tile__heading">
                  <h3 className="lh-tile__title">
                    {tile.title}
                    <span className="lh-sr"> (opens Microsoft Learn in a new tab)</span>
                  </h3>
                  <span aria-hidden="true" className="lh-tile__arrow">
                    ↗
                  </span>
                </div>
                <span className="lh-tile__hint">{tile.hint[viewLevel]}</span>
                <span aria-hidden="true" className="lh-tile__dot" />
              </a>
            ) : (
              <a className="lh-tile" href={tile.to} data-testid="intent-tile">
                <div className="lh-tile__heading">
                  <h3 className="lh-tile__title">{tile.title}</h3>
                  <span aria-hidden="true" className="lh-tile__arrow">
                    ↗
                  </span>
                </div>
                <span className="lh-tile__hint">{tile.hint[viewLevel]}</span>
                <span aria-hidden="true" className="lh-tile__dot" />
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── "Learn with Microsoft Learn" — search + topic pills + ONE list ───────────────────────────
/**
 * The three-way merge. "Search Microsoft Learn", "Browse by topic" and the level-lensed feed each
 * rendered their OWN copy of the same item list — the same links, three times down the page. Here
 * they are one list with two controls above it: the search field in the section's head row (the only
 * place a query sits next to what it drives) and the topic pills beneath.
 *
 * The topic filter applies to whichever list is showing, search results included, so neither control
 * silently stops meaning anything. The pills are built from the CACHED feed, not from the results, so
 * the row does not churn as a search lands.
 */
function LearnSection({
  learn,
  viewLevel,
}: {
  learn: HubPayload['learn'];
  viewLevel: CompetencyLevel;
}) {
  const items = learn.items;
  const [q, setQ] = useState('');
  const [results, setResults] = useState<LearnItem[]>([]);
  const [source, setSource] = useState<'live' | 'cache' | null>(null);
  const [busy, setBusy] = useState(false);
  const [topicKey, setTopicKey] = useState<string | null>(null);

  const query = q.trim();
  const searching = query.length > 0;

  useEffect(() => {
    // Empty query: the cached feed is the no-query surface, so there is nothing to fetch.
    if (query.length === 0) {
      setResults([]);
      setSource(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      void searchLearn(query).then((res) => {
        if (cancelled) return;
        if (res.kind === 'ok') {
          setResults(res.body.items.slice(0, 12));
          setSource(res.body.source);
        } else {
          // A failed call falls back to the cached items we were seeded with (never a broken field).
          const q2 = query.toLowerCase();
          setResults(
            items
              .filter(
                (it) =>
                  it.title.toLowerCase().includes(q2) || it.summary.toLowerCase().includes(q2),
              )
              .slice(0, 12),
          );
          setSource('cache');
        }
        setBusy(false);
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, items]);

  const topics = buildTopics(items);
  const active = topicKey === null ? null : (topics.find((t) => t.key === topicKey) ?? null);

  const base = searching ? results : items;
  const filtered = active
    ? base.filter((it) => it.productTags.some((s) => active.slugs.includes(s)))
    : base;
  // The level lens re-orders the browse list: items matching the viewed level surface first. It NEVER
  // hides an item (LH-NFR04). Search results keep the server's own relevance order.
  const shown = searching
    ? filtered
    : [...filtered].sort(
        (a, b) => (a.level === viewLevel ? 0 : 1) - (b.level === viewLevel ? 0 : 1),
      );

  const stale = isStale(learn.oldestFetchedAt);
  const refreshFailing = learn.lastRun?.outcome === 'error';

  /** One line of state, at most: what the search did, or why the cache may be old. */
  const note = searching
    ? busy
      ? 'Searching…'
      : source === 'cache'
        ? 'Microsoft Learn is slow to respond — showing saved results.'
        : source === 'live'
          ? 'Live from Microsoft Learn, tuned to your role and level.'
          : null
    : refreshFailing
      ? `A refresh is currently failing, so this may be out of date — the links still work.${
          learn.oldestFetchedAt ? ` Last updated ${shortDate(learn.oldestFetchedAt)}.` : ''
        }`
      : stale
        ? `This may be out of date.${
            learn.oldestFetchedAt ? ` Last updated ${shortDate(learn.oldestFetchedAt)}.` : ''
          }`
        : null;

  return (
    <section aria-labelledby="learn-heading" className="lh-section">
      <div className="lh-section-head">
        <h2 id="learn-heading">Learn with Microsoft Learn</h2>
      </div>

      <div className="lh-controls">
        <div className="lh-controls__row">
          {topics.length > 0 && (
            <div className="lh-pills" role="group" aria-label="Filter by topic">
              <Pill
                as="button"
                className="lh-pill"
                aria-pressed={topicKey === null}
                onClick={() => setTopicKey(null)}
              >
                All topics
              </Pill>
              {topics.map((t) => (
                <Pill
                  key={t.key}
                  as="button"
                  className="lh-pill"
                  aria-pressed={topicKey === t.key}
                  onClick={() => setTopicKey(t.key)}
                >
                  {t.label}
                </Pill>
              ))}
            </div>
          )}
          <SearchInput
            size="sm"
            block
            label="Search Microsoft Learn"
            value={q}
            onChange={setQ}
            className="w-[320px] max-w-full shrink-0 lh-controls__trailing"
          />
        </div>
        {note && (
          <p className="lh-note" role="status">
            {note}
          </p>
        )}
      </div>

      {shown.length === 0 ? (
        // `busy` covers the 300ms debounce plus the round trip, during which `note` above is
        // already saying "Searching…" — without this branch the page asserts there is nothing
        // to find from the first keystroke, then contradicts itself when the results land.
        busy ? null : (
          <p className="lh-note">
            {searching ? 'No matching content.' : 'No learning content is available right now.'}
          </p>
        )
      ) : (
        <ul className="lh-tiles">
          {shown.map((it) => (
            <LearnLink key={it.itemRef} item={it} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One outbound Microsoft Learn link (LH-F05/F13). States plainly that it leaves the platform, opens
 * in a new tab with rel="noopener", flags non-English content (LH-F06), and fires a fire-and-forget
 * recordLinkOpen on click — navigation never waits on the record. Renders its own <li> so the list
 * has no wrapper element between the grid and its rows.
 */
function LearnLink({ item }: { item: LearnItem }) {
  const nonEnglish = item.language && !item.language.toLowerCase().startsWith('en');
  return (
    <li>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="lh-tile"
        onClick={() => recordLinkOpen(item.itemRef)}
      >
        <p className="lh-tile__kicker">{item.level}</p>
        <div className="lh-tile__heading">
          <h3 className="lh-tile__title">
            {item.title}
            {/* Off-screen text, not `aria-label` on the glyph's span: a classless <span> maps to
                ARIA `generic`, where `aria-label` is prohibited and unreliably exposed, so the
                name would fall back to "↗". This is the pattern the intent tiles already use. */}
            <span className="lh-sr"> (opens in a new tab, leaves Trailblazer)</span>
          </h3>
          <span aria-hidden="true" className="lh-tile__arrow">
            ↗
          </span>
        </div>
        {item.summary && <p className="lh-tile__hint">{item.summary}</p>}
        <p className="lh-tile__meta">
          {[
            nonEnglish ? `In ${item.language}` : null,
            'Opens Microsoft Learn — leaves the platform',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <span aria-hidden="true" className="lh-tile__dot" />
      </a>
    </li>
  );
}

// ── "Real examples" — genuine, approved catalogue tools (LH-F16) ─────────────────────────────

/**
 * Fetched lazily so it never blocks the page's first paint, and it degrades SILENTLY: when the
 * catalogue feature is off (Dataverse flag) the endpoint returns an empty, disabled payload and this
 * renders nothing at all rather than an empty-state nag. A fetch error also just hides it — examples
 * are a bonus, never load-bearing for the hub.
 */
function RealExamples() {
  const [payload, setPayload] = useState<ExamplesPayload | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const res = await getExamples();
      if (live && res.kind === 'ok') setPayload(res.body);
    })();
    return () => {
      live = false;
    };
  }, []);

  if (!payload || !payload.enabled || payload.examples.length === 0) return null;

  return (
    <section aria-labelledby="examples-heading" className="lh-section">
      <div className="lh-section-head">
        <h2 id="examples-heading">Real examples</h2>
      </div>
      <ul className="lh-tiles">
        {payload.examples.map((ex) => (
          <li key={ex.toolId}>
            <a className="lh-tile" href={`/tools/${ex.toolId}`}>
              <div className="lh-tile__heading">
                <h3 className="lh-tile__title">{ex.name}</h3>
                <span aria-hidden="true" className="lh-tile__arrow">
                  ↗
                </span>
              </div>
              {ex.description && <p className="lh-tile__hint">{ex.description}</p>}
              {/* AE-F19: the same sentence, from the same formatter, as the catalogue card and the
                  tool page — and the same NUMBER, because /api/learning/examples maps the catalogue's
                  own `listCatalogueTools` page. */}
              <p className="lh-tile__meta">
                {ex.ownerName} · {ex.trustLabel} · {formatAdopterCount(ex.adoptionCount)}
              </p>
              <span aria-hidden="true" className="lh-tile__dot" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
